import React, { PropsWithChildren, useCallback, useState } from 'react';
import {
  ContextMenu,
  ContextMenuProps,
  FloatingWrapperProps
} from './ContextMenu';
import { isMenuEntry, isMenuGroup, MenuConfig, MenuEntry } from './NestedMenu';
import { VBox } from '../../templates/layout/Flex';
import { Divider } from '../../atoms/Divider/Divider';
import { ContextMenuListItem } from './ContextMenuListItem';
import { Typography } from '../../atoms/Typography';
import { SizeVariants } from '../../../theme/themes';
import { isMobile } from 'react-device-detect';
import { MobileSlideSurface } from '../../../components/Surface/MobileSlideSurface';

interface SubMenuProps
  extends Pick<FloatingWrapperProps, 'placement' | 'offset'>,
    Pick<ContextMenuProps, 'scrollbarHeight'> {
  /** The trigger element of the current menu (for example: `...` button or the hovered menu item) */
  anchor: HTMLElement;
  menuConfig: MenuConfig;
  setAnchor?: (ref) => void;
  onClose?: () => void;
}
interface SubmenuContainerProps
  extends PropsWithChildren,
    Pick<FloatingWrapperProps, 'placement' | 'offset'>,
    Pick<ContextMenuProps, 'scrollbarHeight'> {
  onClose?: () => void;
  mouseEnter?: () => void;
  mouseLeave?: () => void;
  anchor: HTMLElement;
}

/**
 * switch the submenu container bases on ismobile
 * so it will be displayed inside a slider for the mobile version
 */
const SubmenuContainer = (props: SubmenuContainerProps) => {
  const { children, onClose, mouseEnter, mouseLeave, anchor, ...popupProps } =
    props;

  return (
    <>
      {isMobile ? (
        <MobileSlideSurface onClose={onClose}>{children}</MobileSlideSurface>
      ) : (
        <div onMouseEnter={mouseEnter} onMouseLeave={mouseLeave}>
          <ContextMenu anchor={anchor} {...popupProps}>
            {children}
          </ContextMenu>
        </div>
      )}
    </>
  );
};

export const Submenu = (props: SubMenuProps) => {
  const { anchor, onClose, menuConfig, setAnchor, ...popupProps } = props;

  /** The trigger element of the submenu */
  const [triggerEl, setTriggerEl] = useState<HTMLDivElement>(null);

  const clearAnchor = useCallback(() => {
    setAnchor?.(null);
  }, []);

  const addAnchor = useCallback(() => {
    setAnchor?.(anchor);
  }, [anchor]);

  const onClickMenuItem = useCallback(
    (s: MenuEntry) => {
      return (e) => {
        s.onClick?.(e);
        onClose?.();
      };
    },
    [onClose]
  );

  const clearTriggerEl = useCallback(() => {
    setTriggerEl(null);
  }, []);

  // When the mouse leaves the hover option in the parent menu, the trigger element will be set to null, which will close its submenu,
  // to keep the submenu open, we set the trigger element back when the user hovers over the submenu
  const setTriggerElCallback = useCallback((s: MenuEntry) => {
    return (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (e?.currentTarget && s.children) {
        setTriggerEl(e.currentTarget);
      }
    };
  }, []);

  if (!menuConfig?.length || !anchor) return null;
  return (
    <SubmenuContainer
      onClose={onClose}
      mouseEnter={addAnchor}
      mouseLeave={clearAnchor}
      anchor={anchor}
      {...popupProps}
    >
      <VBox gap={8}>
        {menuConfig.map((item, idx) =>
          isMenuEntry(item) ? (
            item.isDivider ? (
              <Divider />
            ) : (
              <div
                key={item.title + item.icon}
                onMouseEnter={setTriggerElCallback(item)}
                onMouseLeave={clearTriggerEl}
              >
                {item.children && triggerEl && (
                  <Submenu
                    key={'submenu_entry_' + idx + item.title}
                    {...props}
                    menuConfig={item.children}
                    anchor={triggerEl}
                    setAnchor={setTriggerEl}
                    offset={0} // make the submenu overlap slightly with the previous menu
                    placement="left-start"
                  />
                )}
                <ContextMenuListItem
                  {...item}
                  leftIcon={item.icon}
                  name={item.title}
                  modifiers={item.shortCut}
                  showChevron={!!item.children && !item.rightIcon}
                  onClick={onClickMenuItem(item)}
                />
              </div>
            )
          ) : isMenuGroup(item) ? (
            <VBox gap={8}>
              <div style={{ padding: '0px 8px' }}>
                <Typography
                  colorVariant="01"
                  lineHeight="loose"
                  weight="label"
                  variant={SizeVariants.md}
                >
                  {item.groupName}
                </Typography>
              </div>
              <VBox gap={4}>
                {item.entries.map((c, index) => (
                  <div
                    key={c.title}
                    data-pw={c.title}
                    onMouseEnter={setTriggerElCallback(c)}
                    onMouseLeave={clearTriggerEl}
                  >
                    {c.children && triggerEl && (
                      <Submenu
                        {...popupProps}
                        key={'submenu_group_' + index + c.title}
                        menuConfig={c.children}
                        anchor={triggerEl}
                        setAnchor={setTriggerEl}
                        offset={0} // make the submenu overlap slightly with the previous menu
                        placement="left-start"
                      />
                    )}
                    <ContextMenuListItem
                      {...c}
                      name={c.title}
                      leftIcon={c.icon}
                      showChevron={!!c.children && !c.rightIcon}
                      onClick={onClickMenuItem(c)}
                    />
                  </div>
                ))}
              </VBox>
              {idx < menuConfig.length - 1 && (
                <Divider color="line3" length={240} />
              )}
            </VBox>
          ) : null
        )}
      </VBox>
    </SubmenuContainer>
  );
};
